"use client";

import { rmcClient } from "@/lib/rmcClient";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useReactToPrint } from "react-to-print";

const PurchasePartiesListPrint = forwardRef(function PurchasePartiesListPrint(_, ref) {
  const contentRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [snapshot, setSnapshot] = useState(null);

  useEffect(() => {
    (async () => {
      const c = (await rmcClient.getCompany()).data;
      setCompany(c);
    })();
  }, []);

  const filterLabel =
    snapshot?.filter === "inactive" ? "Inactive parties" : "Active parties";

  const printFn = useReactToPrint({
    contentRef,
    documentTitle: `purchase-parties-${snapshot?.filter === "inactive" ? "inactive" : "active"}`,
  });

  useImperativeHandle(ref, () => ({
    openPrint: async (params) => {
      const rows = (await rmcClient.listPurchaseParties(params)).data;
      const isInactive = params?.is_active === false;
      flushSync(() => {
        setSnapshot({ rows: Array.isArray(rows) ? rows : [], filter: isInactive ? "inactive" : "active" });
      });
      await new Promise((resolve) => setTimeout(resolve, 0));
      printFn();
    },
  }));

  const companyName = company?.name || company?.company_name || "";
  const parties = snapshot?.rows ?? [];

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed left-0 top-0 -z-10 h-0 w-0 overflow-hidden opacity-0 print:static print:z-50 print:h-auto print:w-auto print:overflow-visible print:opacity-100"
    >
      <div ref={contentRef} className="bg-white p-6 text-black print:block">
        <div className="mx-auto max-w-[210mm]">
          {companyName ? (
            <p className="mb-1 text-center text-sm font-bold">{companyName}</p>
          ) : null}
          <h1 className="mb-1 text-center text-base font-bold">Purchase parties</h1>
          <p className="mb-4 text-center text-xs text-slate-700">{filterLabel}</p>
          <table className="w-full border-collapse border border-slate-800 text-xs">
            <thead>
              <tr className="bg-slate-100">
                <th className="border border-slate-800 px-2 py-1.5 text-left font-semibold">
                  Purchase party ID
                </th>
                <th className="border border-slate-800 px-2 py-1.5 text-left font-semibold">
                  Purchase party name
                </th>
              </tr>
            </thead>
            <tbody>
              {parties.length === 0 ? (
                <tr>
                  <td colSpan={2} className="border border-slate-800 px-2 py-2 text-slate-600">
                    No records
                  </td>
                </tr>
              ) : (
                parties.map((row) => (
                  <tr key={row.id}>
                    <td className="border border-slate-800 px-2 py-1.5 tabular-nums">{row.id}</td>
                    <td className="border border-slate-800 px-2 py-1.5">{row.name ?? "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});

PurchasePartiesListPrint.displayName = "PurchasePartiesListPrint";

export default PurchasePartiesListPrint;
